<template>
  <v-card flat>
    <v-card-text class="pa-0">
      <div class="d-flex justify-end px-4 pt-3">
        <v-btn small color="primary" outlined @click="toggleDisplayUnit">
          單位: {{ displayUnit === "hours" ? "小時" : "天" }}
          <v-icon small class="ml-1">mdi-swap-horizontal</v-icon>
        </v-btn>
      </div>
      <div class="py-3 mx-4">
        <info-dialog
          :visible.sync="infoDialog.visible"
          :title="infoDialog.title"
          :message="infoDialog.message"
          yes-button-text="確定"
          :yes-callback="infoDialog.yesCallback"
        />
        <div
          v-if="!readOnly"
          class="d-flex align-center justify-space-between flex-wrap mb-2"
        >
          <div class="d-flex align-center">
            <v-select
              v-model="selectedStatus"
              :items="approvalStatusOptions"
              label="審核狀態"
              dense
              outlined
              hide-details
              class="mr-4"
              style="max-width: 150px"
              @change="onStatusChange"
            ></v-select>
            <div class="text-body-2 grey--text text--darken-1">
              已選 {{ selectedRecordIds.length }} 筆紀錄
            </div>
          </div>
          <div class="d-flex align-center action-buttons">
            <v-btn
              class="
                font-weight-bold
                text-capitalize
                btn-primary
                bg-gradient-primary
                py-3
                px-6
                mr-2
              "
              :disabled="!selectedRecordIds.length || bulkActionLoading"
              :loading="bulkActionLoading && bulkActionType === 'approve'"
              @click="confirmBulkApprove"
            >
              審核通過
            </v-btn>
            <v-btn
              class="
                font-weight-bold
                text-capitalize
                btn-ls btn-secondary
                bg-gradient-light
                py-3
                px-6
                ml-2
              "
              text
              :disabled="!selectedRecordIds.length || bulkActionLoading"
              :loading="bulkActionLoading && bulkActionType === 'reject'"
              @click="confirmBulkReject"
            >
              審核拒絕
            </v-btn>
          </div>
        </div>
        <v-skeleton-loader
          v-if="loading"
          type="table"
          class="mb-4"
        ></v-skeleton-loader>

        <div v-else-if="!records.length" class="text-center py-8">
          <v-icon size="64" color="grey lighten-1">mdi-calendar-blank</v-icon>
          <div class="text-h6 text-h4 mt-2">沒有特休假紀錄</div>
          <div class="text-body-2 text-h4">沒有特休假紀錄可以顯示</div>
        </div>

        <template v-else>
          <v-data-table
            :headers="headers"
            :items="records"
            :loading="loading"
            :server-items-length="totalItems"
            :items-per-page="itemsPerPage"
            hide-default-footer
            disable-pagination
            @page-count="pageCount = $event"
            :show-select="!readOnly"
            v-model="selectedRecordIds"
            item-key="id"
          >
            <template v-slot:header.data-table-select="{ on, props }">
              <v-checkbox
                v-bind="props"
                v-on="on"
                :input-value="isPageSelected"
                :indeterminate="isPageIndeterminate"
                hide-details
                dense
                @click.stop="toggleSelectAllOnPage"
              ></v-checkbox>
            </template>

            <template v-slot:item.user_name="{ item }">
              {{ item.user && item.user.name ? item.user.name : "-" }}
            </template>

            <template v-slot:item.expected_sign_date="{ item }">
              <formatted-date
                v-if="getExpectedSignDate(item)"
                :date="getExpectedSignDate(item)"
              />
              <span v-else>-</span>
            </template>

            <template v-slot:item.expected_resign_date="{ item }">
              <formatted-date
                v-if="getExpectedResignDate(item)"
                :date="getExpectedResignDate(item)"
              />
              <span v-else>-</span>
            </template>

            <template v-slot:item.start_time="{ item }">
              <formatted-date :date="item.start_time" />
            </template>

            <template v-slot:item.end_time="{ item }">
              <formatted-date :date="item.end_time" />
            </template>

            <template v-slot:item.leave_minutes="{ item }">
              {{ formatMinutesToDaysString(item.leave_minutes) }}
            </template>

            <template v-slot:item.remaining_minutes="{ item }">
              {{ formatMinutesToDaysString(item.remaining_minutes) }}
            </template>

            <template v-slot:item.reason="{ item }">
              {{ item.reason || "-" }}
            </template>

            <template v-slot:item.approval_status="{ item }">
              {{ item.approval_status || "-" }}
            </template>

            <template v-slot:item.status="{ item }">
              {{ item.status || "-" }}
            </template>
          </v-data-table>

          <div class="text-center my-4">
            <v-pagination
              v-model="currentPage"
              :length="pageCount"
              @input="handlePageChange"
              prev-icon="fa fa-angle-left"
              next-icon="fa fa-angle-right"
              color="#cb0c9f"
              circle
            ></v-pagination>
          </div>
        </template>
        <v-dialog v-model="showConfirmDialog" max-width="420">
          <v-card>
            <v-card-title class="headline">{{
              confirmDialog.title
            }}</v-card-title>
            <v-card-text>{{ confirmDialog.message }}</v-card-text>
            <v-card-actions class="justify-end">
              <v-btn
                text
                color="grey"
                :disabled="bulkActionLoading"
                @click="closeConfirmDialog"
              >
                取消
              </v-btn>
              <v-btn
                color="primary"
                text
                class="text-capitalize"
                :loading="bulkActionLoading"
                @click="executeConfirmedAction"
              >
                確認
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import InfoDialog from "@/components/Common/InfoDialog.vue";
import FormattedDate from "@/components/FormattedDate.vue";

export default {
  name: "AnnualLeaveRecordsWidget",
  components: { FormattedDate, InfoDialog },
  props: {
    selectedCompany: {
      type: Object,
      default: () => null,
    },
    year: {
      type: [String, Number],
      default: () => new Date().getFullYear(),
    },
    selectedUser: {
      type: Object,
      default: () => null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      records: [],
      currentPage: 1,
      pageCount: 0,
      itemsPerPage: 15,
      totalItems: 0,
      selectedRecordIds: [],
      bulkActionLoading: false,
      bulkActionType: null,
      showConfirmDialog: false,
      confirmDialog: {
        title: "",
        message: "",
        action: null,
      },
      approvalStatusOptions: ["待審核", "審核通過", "審核退回"],
      selectedStatus: "待審核",
      infoDialog: {
        visible: false,
        title: "",
        message: "",
        yesCallback: null,
      },
      displayUnit: localStorage.getItem("leave_duration_unit") || "hours",
    };
  },
  computed: {
    isPageSelected() {
      if (!this.records.length) return false;
      const selectedIds = this.selectedRecordIds.map((item) => item.id);
      return this.records.every((record) => selectedIds.includes(record.id));
    },
    isPageIndeterminate() {
      if (!this.records.length) return false;
      const selectedIds = this.selectedRecordIds.map((item) => item.id);
      const selectedCount = this.records.filter((record) =>
        selectedIds.includes(record.id)
      ).length;
      return selectedCount > 0 && selectedCount < this.records.length;
    },
    unitText() {
      return this.displayUnit === "hours" ? "小時" : "天";
    },
    headers() {
      return [
        {
          text: "員工",
          value: "user_name",
          sortable: false,
        },
        {
          text: "到職日期",
          value: "expected_sign_date",
          sortable: false,
        },
        {
          text: "離職日期",
          value: "expected_resign_date",
          sortable: false,
        },
        {
          text: "開始時間",
          value: "start_time",
          sortable: false,
        },
        {
          text: "結束時間",
          value: "end_time",
          sortable: false,
        },
        {
          text: this.unitText,
          value: "leave_minutes",
          sortable: false,
        },
        {
          text: `可用${this.unitText}`,
          value: "remaining_minutes",
          sortable: false,
        },
        {
          text: "事由",
          value: "reason",
          sortable: false,
        },
        {
          text: "審核狀態",
          value: "approval_status",
          sortable: false,
        },
        // {
        //   text: "假期狀態",
        //   value: "status",
        //   sortable: false,
        // },
      ];
    },
  },
  mounted() {
    this.fetchAnnualLeaves();
    window.addEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  beforeDestroy() {
    window.removeEventListener(
      "leave-duration-unit-changed",
      this.syncDisplayUnit
    );
  },
  watch: {
    selectedCompany: {
      handler() {
        this.currentPage = 1;
        this.fetchAnnualLeaves();
      },
      deep: true,
    },
    records() {
      const validIds = new Set(this.records.map((record) => record.id));
      this.selectedRecordIds = this.selectedRecordIds.filter((item) =>
        validIds.has(item.id)
      );
    },
  },
  methods: {
    formatMinutesToDaysString(minutes) {
      if (minutes == null) return "0";
      const val = parseFloat(minutes);
      if (isNaN(val)) return "0";
      if (this.displayUnit === "hours") {
        const hours = val / 60;
        return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
      }
      const days = val / 480;
      return Number.isInteger(days) ? days.toString() : days.toFixed(1);
    },
    syncDisplayUnit() {
      this.displayUnit = localStorage.getItem("leave_duration_unit") || "hours";
    },
    toggleDisplayUnit() {
      this.displayUnit = this.displayUnit === "hours" ? "days" : "hours";
      localStorage.setItem("leave_duration_unit", this.displayUnit);
      window.dispatchEvent(new Event("leave-duration-unit-changed"));
    },
    async fetchAnnualLeaves() {
      this.loading = true;
      try {
        const params = new URLSearchParams({
          page: String(this.currentPage),
          limit: String(this.itemsPerPage),
        });
        if (this.selectedCompany && this.selectedCompany.id) {
          params.append("company_id", this.selectedCompany.id);
          // this.selectedUser
          if (this.selectedUser && this.selectedUser.id) {
            params.append("user_id", this.selectedUser.id);
          }
        }
        params.append("approval_status", this.selectedStatus);

        // 使用新 API /api/annual-leaves
        const response = await this.$axios.get(`/api/annual-leaves?${params}`);
        const payload = response && response.data ? response.data : {};
        const data = Array.isArray(payload.data) ? payload.data : [];
        // 為了相容於舊的 template，將 total_minutes 映射到 leave_minutes
        this.records = data.map((r) => ({
          ...r,
          leave_minutes: r.total_minutes,
          remaining_minutes: r.remaining_minutes,
        }));

        if (payload.pagination) {
          this.totalItems = payload.pagination.totalItems || 0;
          this.pageCount = payload.pagination.totalPages || 1;
        } else {
          this.totalItems = data.length;
          this.pageCount = 1;
        }
      } catch (error) {
        this.records = [];
        this.totalItems = 0;
        this.emitWidgetError("取得特休假紀錄失敗", error);
      } finally {
        this.loading = false;
      }
    },
    handlePageChange() {
      this.fetchAnnualLeaves();
    },
    onStatusChange() {
      this.currentPage = 1;
      this.fetchAnnualLeaves();
    },

    toggleSelectAllOnPage() {
      if (this.isPageSelected) {
        // Deselect all on current page
        const pageIds = this.records.map((r) => r.id);
        this.selectedRecordIds = this.selectedRecordIds.filter(
          (item) => !pageIds.includes(item.id)
        );
      } else {
        // Select all on current page
        // Add only records that are not already in selectedRecordIds
        const selectedIds = this.selectedRecordIds.map((item) => item.id);
        const newRecords = this.records.filter(
          (r) => !selectedIds.includes(r.id)
        );
        this.selectedRecordIds = [...this.selectedRecordIds, ...newRecords];
      }
    },
    confirmBulkApprove() {
      if (!this.selectedRecordIds.length || this.bulkActionLoading) return;
      this.confirmDialog = {
        title: "確認審核通過",
        message: `確定要審核通過這 ${this.selectedRecordIds.length} 筆紀錄嗎？ 將把會其他特休假取消`,
        action: this.bulkApprove,
      };
      this.showConfirmDialog = true;
    },
    confirmBulkReject() {
      if (!this.selectedRecordIds.length || this.bulkActionLoading) return;
      this.confirmDialog = {
        title: "確認審核拒絕",
        message: `確定要審核拒絕這 ${this.selectedRecordIds.length} 筆紀錄嗎？`,
        action: this.bulkReject,
      };
      this.showConfirmDialog = true;
    },
    closeConfirmDialog() {
      if (this.bulkActionLoading) return;
      this.showConfirmDialog = false;
      this.confirmDialog = { title: "", message: "", action: null };
    },
    async executeConfirmedAction() {
      if (typeof this.confirmDialog.action === "function") {
        await this.confirmDialog.action();
      }
      this.closeConfirmDialog();
    },
    async bulkApprove() {
      if (!this.selectedRecordIds.length) return;
      this.bulkActionType = "approve";
      this.bulkActionLoading = true;
      try {
        // v-data-table v-model returns selected items (objects)
        const uniqueIds = this.selectedRecordIds.map((item) => item.id);
        console.log(uniqueIds);
        const response = await this.$axios.post("/api/annual-leaves/approve", {
          ids: uniqueIds,
        });
        this.selectedRecordIds = [];
        const payload = response?.data?.data || {};
        const approvedCount = payload?.approved_count ?? uniqueIds.length;
        const skippedCount = payload?.skipped_count ?? 0;
        const failedCount = payload?.failed_count ?? 0;
        const summaryMessage = `成功審核通過 ${approvedCount} 筆，已略過 ${skippedCount} 筆，失敗 ${failedCount} 筆。`;
        this.infoDialog.title = "審核通過結果";
        this.infoDialog.message = summaryMessage;
        this.infoDialog.yesCallback = async () => {
          this.infoDialog.yesCallback = null;
          await this.fetchAnnualLeaves();
        };
        this.infoDialog.visible = true;
      } catch (error) {
        this.emitWidgetError("審核通過失敗", error);
      } finally {
        this.bulkActionLoading = false;
        this.bulkActionType = null;
      }
    },
    async bulkReject() {
      if (!this.selectedRecordIds.length) return;
      this.bulkActionType = "reject";
      this.bulkActionLoading = true;
      try {
        const uniqueIds = this.selectedRecordIds.map((item) => item.id);
        const response = await this.$axios.post("/api/annual-leaves/reject", {
          ids: uniqueIds.join(","),
        });
        const payload = response?.data?.data || {};
        const rejectedCount = payload?.rejected_count ?? uniqueIds.length;
        const skippedCount = payload?.skipped_count ?? 0;
        const failedCount = payload?.failed_count ?? 0;
        const summaryMessage = `成功拒絕 ${rejectedCount} 筆，已略過 ${skippedCount} 筆，失敗 ${failedCount} 筆。`;
        this.selectedRecordIds = [];
        this.infoDialog.title = "審核拒絕結果";
        this.infoDialog.message = summaryMessage;
        this.infoDialog.yesCallback = async () => {
          this.infoDialog.yesCallback = null;
          await this.fetchAnnualLeaves();
        };
        this.infoDialog.visible = true;
      } catch (error) {
        this.emitWidgetError("審核拒絕失敗", error);
      } finally {
        this.bulkActionLoading = false;
        this.bulkActionType = null;
      }
    },
    emitWidgetError(title, error) {
      const message =
        (error &&
          (error.response?.data?.message ||
            error.response?.data?.error ||
            error.message)) ||
        "請稍後再試";
      this.$emit("error", {
        title,
        message,
      });
    },
    getJoinTypeValue(record) {
      const profileJoinType =
        record?.user?.employeeProfile?.join_type ??
        record?.user?.employee_profile?.join_type;
      if (profileJoinType !== undefined && profileJoinType !== null) {
        return profileJoinType;
      }
      return record?.join_type;
    },
    getExpectedSignDate(record) {
      return (
        record?.user?.employeeProfile?.expected_sign_date ||
        record?.user?.employee_profile?.expected_sign_date ||
        record?.expected_sign_date ||
        null
      );
    },
    getExpectedResignDate(record) {
      return (
        record?.user?.employeeProfile?.expected_resign_date ||
        record?.user?.employee_profile?.expected_resign_date ||
        record?.expected_resign_date ||
        null
      );
    },
    getJoinTypeText(joinType) {
      if (joinType === 1) return "正職";
      if (joinType === 2) return "兼職";
      if (joinType === null || joinType === undefined) return "-";
      return String(joinType);
    },
  },
};
</script>

<style scoped>
.reason-column {
  max-width: 30%;
  width: 30%;
}
.checkbox-column {
  width: 48px;
}
.selected-row {
  background-color: rgba(203, 12, 159, 0.06);
}
.action-buttons > * {
  min-width: 110px;
}
</style>
